/**
 * Manual Queries page tour — /operator/manual-queries (and admin/*).
 *
 * Steps 0-2  → list tab (mq-tab-switcher, mq-query-list, mq-tab-new)
 * Steps 3+   → create tab (auto-switched via fk:tour:step event in ManualQueriesPage)
 *
 * data-tour anchors:
 *   mq-tab-switcher    Tab switcher bar
 *   mq-tab-list        "All Queries" tab button
 *   mq-tab-new         "New Query" tab button
 *   mq-query-list      List of existing queries
 *   mq-paste-parser    AI Paste & auto-fill card (drag/paste/upload)
 *   mq-form-route      Route & reference section
 *   mq-form-packages   Cargo / packages section
 *   mq-form-customer   Customer details section (right column)
 *   mq-form-submit     Submit button
 *
 * Media (optional): drop GIFs at public/walkthrough/manual-query/step-<N>.gif —
 * see public/walkthrough/README.md.
 */

const M = (step, alt, caption) => ({
  src: `/walkthrough/manual-query/step-${step}.gif`,
  alt,
  caption,
});

export const manualQueryTour = {
  id: "manual-query",
  label: "Manual Query Creation",
  steps: [
    // ── List tab (steps 0-2) ──────────────────────────────────────────────────
    {
      target: "mq-tab-switcher",
      title: "Two views on one page",
      description:
        "This page has two sections. 'All Queries' shows existing work. 'New Query' is where you create a fresh freight request. You're on the list right now.",
      placement: "bottom",
      media: M(0, "List vs New tab switcher"),
    },
    {
      target: "mq-query-list",
      title: "Your existing queries",
      description:
        "Each card is a freight query with its current status, route, and customer. The coloured dot shows where it is — amber means in progress, green means complete. Click any card to open the full details.",
      hint: "Use the search bar to filter by subject, route, or customer name.",
      placement: "bottom",
      media: M(1, "Query list"),
    },
    {
      target: "mq-tab-new",
      title: "Ready to create one?",
      description:
        "Click 'New Query' to open the creation form. The tour will walk you through every field — just follow the steps.",
      placement: "bottom",
      media: M(2, "Open New Query tab"),
    },

    // ── Create tab (steps 3+) — page auto-switches at this point ──────────────
    {
      target: "mq-paste-parser",
      title: "Optional — let AI fill the form for you",
      description:
        "Got the enquiry as a forwarded email or a screenshot? Paste it (or drag/drop an image) into this card and click 'Parse with AI'. The model reads it and fills the route, packages, and customer fields below. You can edit anything before submitting.",
      bullets: [
        "Paste plain text from a customer email",
        "Drop or paste a screenshot — PNG, JPG, or WEBP up to 5MB",
        "Click the thumbnail of a dropped image to preview it full-size",
        "Skip entirely and fill the form manually if you prefer",
      ],
      hint: "AI is a shortcut, not a requirement. The form is fully editable below.",
      interactive: true,
      placement: "bottom",
      media: M(3, "AI paste & auto-fill", "Drop a screenshot, click Parse, watch the form fill."),
    },
    {
      target: "mq-form-route",
      title: "Step 1 — route details",
      description:
        "Enter where the shipment is coming from (Origin) and where it's going (Destination). Add a subject line so the query is easy to find later in the list.",
      placement: "bottom",
      media: M(4, "Route inputs"),
    },
    {
      target: "mq-form-packages",
      title: "Step 2 — cargo & packages",
      description:
        "Describe what's being shipped. Add weight, dimensions, and quantity for each package. You can add multiple package lines if the shipment has mixed cargo.",
      hint: "Tick 'Hazardous' if the cargo contains restricted materials — this affects which airlines can carry it.",
      placement: "bottom",
      media: M(5, "Cargo / package rows"),
    },
    {
      target: "mq-form-customer",
      title: "Step 3 — customer details",
      description:
        "Enter the customer's name, email, and phone number. These are used in all communications and templates related to this shipment.",
      placement: "left",
      media: M(6, "Customer details"),
    },
    {
      target: "mq-form-submit",
      title: "Create the query",
      description:
        "Once all required fields are filled, click 'Create Query'. It appears instantly in 'All Queries' and your team can start working on it.",
      hint: "After creating, you're taken to the detail page where you can collect rates and build the cost table.",
      placement: "top",
      media: M(7, "Submit and land on detail page"),
    },
  ],
};
